import { z } from 'zod';

import type { ChatMessageApi } from './types.ts';

// ai-services' job intake writes these (job_intake_summary.job_summary_result, job_intake_tools._created/_saved/_opened).
const jobSummarySchema = z.object({
  type: z.literal('job_summary'),
  /** The intake version the summary shows; creating or saving checks the HM saw it. */
  version: z.number(),
  mode: z.enum(['create', 'draft', 'edit']),
  jobId: z.string().nullable(),
  /** The summary's lines in order, as the HM typed them; the title is "Job title". */
  fields: z.array(z.object({ label: z.string(), value: z.string() })),
  skills: z.array(
    z.object({ display: z.string(), verdict: z.string().nullable() }),
  ),
  signal: z
    .object({ label: z.string(), nextStep: z.string().nullable() })
    .nullable(),
  /** What the card offers: the reply its button sends, such as "create", or edit_manually. */
  actions: z.array(z.string()),
});

const jobSavedSchema = z.object({
  type: z.literal('job_saved'),
  jobId: z.string(),
  sourcingState: z.string(),
  /** The HM asked to edit the job themselves: the panel opens the editor on it. */
  openEditor: z.boolean(),
});

// ai-services' Finlay agent writes these for Slack and the chat alike (app/models/agent_results_DTO.py); every repo
// checks the same example (tests/agentResultsExample.json).

/** A button whose click is the HM's next message: it sends `message`, and Finlay answers it as if they typed it. */
const sayButtonSchema = z.object({ label: z.string(), message: z.string() });

/** A change Finlay prepared, waiting for the HM's yes. Confirm and Cancel run or drop it without asking Finlay again. */
const confirmationSchema = z.object({
  type: z.literal('confirmation'),
  confirmationId: z.string(),
  kind: z.string(),
  /** What the HM agrees to, with any message that goes out. */
  preview: z.string(),
  /** Finlay's one pushback, when the change cuts against the job's must-haves. */
  headsUp: z.string().nullable(),
  confirmLabel: z.string(),
  cancelLabel: z.string(),
  expiresAt: z.string(),
});

/** What became of a confirmation, after a click or a yes typed in the chat. */
const confirmationOutcomeSchema = z.object({
  type: z.literal('confirmation_outcome'),
  confirmationId: z.string(),
  status: z.enum(['done', 'partial', 'cancelled', 'refused', 'failed']),
  /** The confirmation's buttons are done with; false after a failure the HM can try again. */
  final: z.boolean(),
  summary: z.string(),
});

/** A question with a fixed set of answers, such as which of two people called Jana. */
const choiceSchema = z.object({
  type: z.literal('choice'),
  question: z.string(),
  options: z.array(
    z.object({ ref: z.string(), label: z.string(), message: z.string() }),
  ),
});

const resultJobSchema = z.object({ id: z.string(), title: z.string() });

/** One candidate on one job, from Finlay's brief. `actions` are the decisions and messages the HM can start. */
const candidateCardSchema = z.object({
  type: z.literal('candidate_card'),
  ref: z.string(),
  candidateJobId: z.string(),
  candidateId: z.string(),
  name: z.string(),
  headline: z.string().nullable(),
  location: z.string().nullable(),
  job: resultJobSchema,
  stage: z.string(),
  stageLabel: z.string(),
  stageSince: z.string().nullable(),
  fit: z.string().nullable(),
  mustHaves: z.array(z.object({ requirement: z.string(), status: z.string() })),
  practicalFacts: z.array(z.object({ kind: z.string(), text: z.string() })),
  url: z.string().nullable(),
  actions: z.array(sayButtonSchema),
});

const candidateRowSchema = z.object({
  ref: z.string(),
  candidateJobId: z.string(),
  candidateId: z.string().nullable(),
  name: z.string(),
  job: resultJobSchema,
  stage: z.string().nullable(),
  stageLabel: z.string().nullable(),
  headline: z.string().nullable(),
  fit: z.string().nullable(),
  /** Why the row is there, in a sentence: "Submitted 2 days ago, waiting on your decision". */
  detail: z.string().nullable(),
  since: z.string().nullable(),
  url: z.string().nullable(),
  actions: z.array(sayButtonSchema),
  /** Asks Finlay for the candidate's card; the chat opens the candidate in the panel instead. */
  details: sayButtonSchema,
});

/** Candidates by stage, or what waits on the HM. `total` counts them all; `more` asks for the next page. */
const candidateListSchema = z.object({
  type: z.literal('candidate_list'),
  title: z.string(),
  rows: z.array(candidateRowSchema),
  total: z.number(),
  more: sayButtonSchema.nullable(),
});

const chatResultSchema = z.discriminatedUnion('type', [
  jobSummarySchema,
  jobSavedSchema,
  confirmationSchema,
  confirmationOutcomeSchema,
  choiceSchema,
  candidateCardSchema,
  candidateListSchema,
]);

export type JobSummaryResult = z.infer<typeof jobSummarySchema>;
export type JobSavedResult = z.infer<typeof jobSavedSchema>;
export type ConfirmationResult = z.infer<typeof confirmationSchema>;
export type ConfirmationOutcome = z.infer<typeof confirmationOutcomeSchema>;
export type ChoiceResult = z.infer<typeof choiceSchema>;
export type CandidateCardResult = z.infer<typeof candidateCardSchema>;
export type CandidateRowResult = z.infer<typeof candidateRowSchema>;
export type CandidateListResult = z.infer<typeof candidateListSchema>;
export type SayButton = z.infer<typeof sayButtonSchema>;
export type ChatResult = z.infer<typeof chatResultSchema>;
type JobResult = JobSummaryResult | JobSavedResult;

const isJobResult = (result: ChatResult): result is JobResult =>
  result.type === 'job_summary' || result.type === 'job_saved';

/**
 * The structured results a reply carries that the chat can draw. Types it doesn't know, and items that don't match,
 * are skipped rather than breaking the chat: ai-services adds result types without waiting for the web app.
 */
export const parseResults = (results: unknown): ChatResult[] => {
  if (!Array.isArray(results)) return [];
  return results.flatMap((item) => {
    const parsed = chatResultSchema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  });
};

/** The newest job result any completed reply carries, with the reply it came in, for the side panel to show. */
export const latestJobResult = (
  messages: ChatMessageApi[],
): { result: JobResult; messageId: string } | null => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role !== 'Assistant' || message.status !== 'Completed') {
      continue;
    }
    const results = parseResults(message.results).filter(isJobResult);
    const result = results[results.length - 1];
    if (result) return { result, messageId: message.id };
  }
  return null;
};

/**
 * What became of each confirmation the chat shows, by its id: the newest outcome a reply carries, from a click or a
 * yes typed in the chat. A final outcome stays, as nothing can undo it.
 */
export const confirmationOutcomes = (
  messages: ChatMessageApi[],
): Map<string, ConfirmationOutcome> => {
  const outcomes = new Map<string, ConfirmationOutcome>();
  for (const message of completedReplies(messages)) {
    for (const result of parseResults(message.results)) {
      if (result.type !== 'confirmation_outcome') continue;
      if (outcomes.get(result.confirmationId)?.final) continue;
      outcomes.set(result.confirmationId, result);
    }
  }
  return outcomes;
};

/** The newest candidate card any completed reply carries, with the reply it came in. */
export const latestCandidateCard = (
  messages: ChatMessageApi[],
): { result: CandidateCardResult; messageId: string } | null => {
  for (const message of completedReplies(messages).reverse()) {
    const cards = parseResults(message.results).filter(
      (result): result is CandidateCardResult =>
        result.type === 'candidate_card',
    );
    const card = cards[cards.length - 1];
    if (card) return { result: card, messageId: message.id };
  }
  return null;
};

const completedReplies = (messages: ChatMessageApi[]) =>
  messages.filter(
    (message) => message.role === 'Assistant' && message.status === 'Completed',
  );

/** The newest reply that saved a job for the HM to edit themselves, which the panel's editor opens on. */
export const latestEditorRequest = (
  messages: ChatMessageApi[],
): { jobId: string; messageId: string } | null => {
  for (const message of completedReplies(messages).reverse()) {
    const saved = parseResults(message.results).find(
      (result): result is JobSavedResult =>
        result.type === 'job_saved' && result.openEditor,
    );
    if (saved) return { jobId: saved.jobId, messageId: message.id };
  }
  return null;
};

/** The newest reply that finished, which a later reply is told apart from. */
export const newestCompletedReply = (messages: ChatMessageApi[]) =>
  completedReplies(messages).at(-1) ?? null;

/** Whether a reply after the message `afterId` saved the job `jobId`, as Finlay does with a change the HM asks for. */
export const savedAgainAfter = (
  messages: ChatMessageApi[],
  jobId: string,
  afterId: string,
) => {
  const after = messages.findIndex((message) => message.id === afterId);
  return (
    after >= 0 &&
    completedReplies(messages.slice(after + 1)).some((message) =>
      parseResults(message.results).some(
        (result) => result.type === 'job_saved' && result.jobId === jobId,
      ),
    )
  );
};
